import type { GQL_ID } from '@@partner:types:graphql'
import type { Shop, ShopBox, ShopTagInfo, SlotPlanInfo } from '@@partner:types:app'

import type { ShopInfoInput } from '@@partner:graphql/mutation/shop/updateShopInfo.gql'
// import type { Optional } from 'utility-types'

import { mergeLeftDropping } from 'merge-left-utils'

type ShopWithoutDeliveryBoxes = Omit<Shop, 'deliveryBoxes'>

type ShopImage = {
  id: GQL_ID
  url: string
  weight: number
}

export type ShopStateType = Partial<ShopWithoutDeliveryBoxes> &
  Pick<
    Shop, 
    'shopGeoPoint' 
    | 'name' 
    | 'address' 
    | 'phone'
    | 'status' 
    | 'minOrderCost' 
    | 'pickup_delivery_cost'
    | 'has_pickup_delivery_enabled'
    | 'is_serving_wholesale'
    | 'is_serving_retail'
    | 'deliveryDefaultPickupComment'
  > & {
    dateOpenTime?: number
    dateCloseTime?: number
    shopOpenTime?: number
    shopCloseTime?: number
    dateExpressDeliveryStart?: number
    dateExpressDeliveryFinish?: number
    slotPlans: SlotPlanInfo[]
    deliveryBoxes: Omit<ShopBox, 'id'>[]
    images: ShopImage[]
    shopTags?: GQL_ID[]
  }

export const SHOP_DEFAULT_STATE: ShopStateType = {
  xid: undefined,
  name: '',
  address: '',
  phone: '',
  slug: '',
  tg_chat_id: '',
  vkMaxChatId: '',
  status: 0,

  images: [],

  minOrderCost: 0,
  has_pickup_delivery_enabled: false,
  pickup_delivery_cost: 0,
  
  tzOffset: 0,

  shopGeoPoint: {
    latitude: undefined,
    longitude: undefined
  },
  shopOpenTime: 0,
  shopCloseTime: 0,
  dateOpenTime: undefined,
  dateCloseTime: undefined,

  slotPlans: [],

  deliveryBoxes: [],

  is_serving_wholesale: false,
  is_serving_retail: false,
  deliveryDefaultPickupComment: '',
  city_id: '',
  shopTags: []
}

export const makeDefaultState = (id: GQL_ID): ShopStateType =>
  ({
    ...SHOP_DEFAULT_STATE,
    id,
    dateOpenTime: undefined, //representTimeWithDateObject(0),
    dateCloseTime: undefined //representTimeWithDateObject(0)
  } as ShopStateType)

// state and api variable are same excluding info fields
// so drop them and go
export const mapStateToApiVariable = (state: ShopStateType): ShopInfoInput => {
  return mergeLeftDropping(
    [
      'id',
      'dateOpenTime',
      'dateCloseTime',
    ],
    state,
    {
      images: state.images.map(image => ({
        id: image.id,
        weight: image.weight
      })),
      phone: state.phone,
      minOrderCost: Number(state.minOrderCost),
      pickup_delivery_cost: Number(state.pickup_delivery_cost),
      shopOpenTime: state.dateOpenTime,
      shopCloseTime: state.dateCloseTime,
      slotPlans: state.slotPlans.map((slotPlan) => {
        return {
          id: slotPlan.id,
          positionsLimit: Number(slotPlan.positionsLimit) || 0,
          ordersLimit: Number(slotPlan.ordersLimit) || 0,
          slotStartTime: slotPlan.slotStartTime,
          slotFinishTime: slotPlan.slotFinishTime,
          weekDay: Number(slotPlan.weekDay),
          slotTimeout: Number(slotPlan.slotTimeout) * 60000 || 0
        }
      }),
      deliveryDefaultPickupComment: state.deliveryDefaultPickupComment,
      deliveryProviders: state.has_pickup_delivery_enabled ? [ 'pickup' ] : [],
      city_id: state.city_id,
      shopTags: state.shopTags || []
    }
  ) as ShopInfoInput
}

export const mapApiDataToState = (data: Shop): ShopStateType => ({
  ...data,
  images: data.images.map((image, index) => ({
    id: image.id,
    url: image.url,
    weight: index
  })),
  dateOpenTime: data.shopOpenTime,
  dateCloseTime: data.shopCloseTime,
  slotPlans: data.slotPlans?.map((slotPlan) => {
    return {
      id: slotPlan.id,
      positionsLimit: Number(slotPlan.positionsLimit) || 0,
      ordersLimit: Number(slotPlan.ordersLimit) || 0,
      slotStartTime: slotPlan.slotStartTime,
      slotFinishTime: slotPlan.slotFinishTime,
      weekDay: Number(slotPlan.weekDay),
      slotTimeout: slotPlan.slotTimeout / 60000
    }
  }) ?? [],
  deliveryBoxes: data.deliveryBoxes?.map((deliveryBox) => {
    const { id: _, ...otherData} = deliveryBox
    return {
      ...otherData
    }
  }) ?? [],
  city_id: data.city_id || '',
  shopTags: data.shopTags?.map(tag => tag.id) ?? []
})
